import { api } from "@/lib/api";

/** 日付の出どころ。付け替えのときに叩く口がこれで決まる。 */
export type ShipDateSource = "project" | "item";

/**
 * カレンダーのカード 1 枚（API の `ShippingCalendarEntryResponse`）。
 *
 * 日付は**明細の出荷予定日、無ければ案件の出荷予定日**という実効日。そのため
 * 分納の案件は同じ `project_id` のカードが複数の日に並ぶ。
 */
export interface ShippingCalendarEntry {
  /** 実効の出荷予定日（`YYYY-MM-DD`）。 */
  ship_date: string;
  project_id: number;
  project_number: string;
  project_title: string | null;
  client_id: number | null;
  client_name: string | null;
  /** 直送先。null は直送しない（得意先へ納める）。 */
  ship_to_id: number | null;
  ship_to_name: string | null;
  client_order_numbers: string[];
  deadline: string | null;
  /** 出荷日時。null なら未出荷。 */
  shipped_at: string | null;
  is_set_product: boolean;
  /** この日に出る明細の数。明細が 1 行も無い案件では 0。 */
  item_count: number;
  /** 案件全体の明細行数。`item_count` と違えば分納（この日は一部）。 */
  project_item_count: number;
  /**
   * `project` なら案件の出荷予定日で出ている（自分の日を持たない明細がまとめて
   * 動く）。`item` なら明細が自分の出荷予定日を持っている。
   */
  date_source: ShipDateSource;
  /** `date_source=item` のときだけ入る。付け替える対象の明細。 */
  project_item_ids: number[];
}

export interface ShippingCalendarResponse {
  date_from: string;
  date_to: string;
  entries: ShippingCalendarEntry[];
}

export interface ShippingCalendarParams {
  /** 開始日（この日を含む・`YYYY-MM-DD`）。 */
  dateFrom: string;
  /** 終了日（この日を含む）。`dateFrom` からの差は 92 日まで。 */
  dateTo: string;
  clientId?: number;
  /** 未指定なら出荷済み・未出荷の両方。 */
  shipped?: boolean;
}

/**
 * 出荷予定日を期間で引く（カレンダーの 1 画面ぶん）。
 *
 * 月グリッドは前月末・翌月頭のマスを含むので、月ではなく日付の範囲で渡す。
 */
export function listShippingCalendar(
  params: ShippingCalendarParams,
): Promise<ShippingCalendarResponse> {
  const query = new URLSearchParams();
  query.set("from", params.dateFrom);
  query.set("to", params.dateTo);
  if (params.clientId != null) query.set("client_id", String(params.clientId));
  if (params.shipped != null) query.set("shipped", String(params.shipped));
  return api.get<ShippingCalendarResponse>(`/shipping-calendar?${query}`);
}

/** カード 1 枚を見分ける鍵（同じ案件が別の日にも並ぶため案件IDだけでは足りない）。 */
export function entryKey(entry: ShippingCalendarEntry): string {
  return `${entry.project_id}-${entry.ship_date}-${entry.date_source}`;
}
